/**
 * Per-site capabilities — the SPA's view of what a site has, in what
 * configuration, and which sub-endpoints to call to drive it.
 *
 * Sourced from `<site>/wp-json/ezqr/v1/site`, the canonical contract
 * for any feature that exposes site-level state going forward. See
 * memory/site-capabilities-contract.md (project convention) for the
 * raw envelope.
 *
 * Key shape rules the SPA layers on top of the contract:
 *  - A feature key may be **absent** entirely when the plugin isn't
 *    provisioned for the site. Absent ≠ disabled — consumers should
 *    treat "missing" and "enabled: false" differently in messaging
 *    (missing → don't surface a tile at all; disabled → show a
 *    greyed-out tile with `reason`).
 *  - `endpoints` is normalised to a record at the wire boundary —
 *    backend sends `[]` for empty, `{ name: path }` otherwise.
 *  - Unknown future features pass through under their slug as
 *    `Feature<unknown>` so a new backend release can't break parsing.
 */
import { z } from 'zod'

// ---- shared envelope ---------------------------------------------------

/**
 * Build a Zod schema for the generic feature envelope, parameterised
 * by the feature's `config` schema. The other four fields
 * (`enabled`, `visibility`, `reason`, `endpoints`) are uniform across
 * every feature.
 */
export function featureEnvelopeSchema<C extends z.ZodTypeAny>(configSchema: C) {
  return z.object({
    enabled: z.boolean(),
    /**
     * Open enum — only `'public'` observed so far. Kept as string for
     * forward-compat; tighten to z.enum once we learn the full set.
     */
    visibility: z.string(),
    reason: z.string().nullable(),
    config: configSchema,
    /** Empty `{}` when the feature has no actions; non-empty record otherwise. */
    endpoints: z.record(z.string(), z.string()),
  })
}

export type Feature<C> = {
  enabled: boolean
  visibility: string
  reason: string | null
  config: C
  endpoints: Record<string, string>
}

// ---- site identity -----------------------------------------------------

export const SiteIdentitySchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  url: z.string().url(),
  /** Often === `url` today; modelled separately so a future split doesn't break parsing. */
  baseUrl: z.string().url(),
  /** Absent (null) means no custom logo — UI falls back to initials / generic icon. */
  logo: z.string().url().nullable(),
  /** BCP-47 with underscore (`en_US`); not parsed, just round-tripped. */
  locale: z.string(),
  theme: z.object({
    slug: z.string(),
  }),
})
export type SiteIdentity = z.infer<typeof SiteIdentitySchema>

// ---- dual pricing (menu-level feature) ---------------------------------

export const DualPricingConfigSchema = z.object({
  /** Which price is the base ("cash" → card is derived; "card" → cash is derived). */
  basePriceType: z.string(),
  /** Surcharge / discount percent applied at the boundary. */
  feePercent: z.number(),
  /**
   * How the fee applies. Known: `'card_surcharge'`. Other values
   * (e.g. `'cash_discount'`) likely come later — open string for now.
   */
  feeDirection: z.string(),
})
export type DualPricingConfig = z.infer<typeof DualPricingConfigSchema>
export const DualPricingFeatureSchema = featureEnvelopeSchema(DualPricingConfigSchema)
export type DualPricingFeature = z.infer<typeof DualPricingFeatureSchema>

// ---- online ordering (Clover-integrated) -------------------------------

/**
 * Ordering carries its OWN dual-pricing block, with deliberately
 * different field names (`baseType` / `feePct` vs the menu-level
 * `basePriceType` / `feePercent`). These are SEPARATE concerns
 * (menu pricing vs Clover/checkout pricing) and may evolve
 * independently — don't normalise.
 */
export const OrderingDualPricingSchema = z.object({
  enabled: z.boolean(),
  baseType: z.string(),
  feePct: z.number(),
})
export type OrderingDualPricing = z.infer<typeof OrderingDualPricingSchema>

export const OnlineOrderingConfigSchema = z.object({
  /** Clover-side merchant identifier. */
  merchantId: z.string(),
  /** Business name as Clover knows it — usually the legal entity. */
  businessName: z.string(),
  /** Checkout flow variant. Known: `'one'`. Open string. */
  flow: z.string(),
  dualpricing: OrderingDualPricingSchema,
  /**
   * True when the site is in "catalog only" mode — customers can
   * browse but not place orders. Toggled via the
   * `settings_catalog_mode` endpoint.
   */
  catalogMode: z.boolean(),
})
export type OnlineOrderingConfig = z.infer<typeof OnlineOrderingConfigSchema>
export const OnlineOrderingFeatureSchema = featureEnvelopeSchema(OnlineOrderingConfigSchema)
export type OnlineOrderingFeature = z.infer<typeof OnlineOrderingFeatureSchema>

// ---- top-level ---------------------------------------------------------

/**
 * Known feature slugs the SPA models explicitly. Anything else is
 * surfaced as an opaque `Feature<unknown>` so a new backend release
 * doesn't break parsing.
 */
export const FeaturesSchema = z
  .object({
    online_ordering: OnlineOrderingFeatureSchema.optional(),
    dual_pricing: DualPricingFeatureSchema.optional(),
  })
  .catchall(featureEnvelopeSchema(z.unknown()))
export type Features = z.infer<typeof FeaturesSchema>

export const SiteCapabilitiesSchema = z.object({
  /**
   * Forward-compat marker. A bump means the envelope changed in a
   * breaking way — let validation fail loudly rather than silently
   * misparse.
   */
  apiVersion: z.literal(1),
  site: SiteIdentitySchema,
  features: FeaturesSchema,
})
export type SiteCapabilities = z.infer<typeof SiteCapabilitiesSchema>
